import { useFeatureGate as useFeatureGateBase } from '@statsig/react-bindings';
import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useParams as useParamsBase } from 'react-router-dom';

import { Logger, logger as loggerBase } from '../../datadog/logger';
import { useIsScreenBelowBreakpoint as useIsScreenBelowBreakpointBase } from '../../hooks/useIsScreenBelowBreakpoint';
import { useTrackEvent as useTrackEventBase } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { telehealthAutoOpenIncompleteMeasuresDrawer } from '../../statsig/gates';
import {
  OutstandingMeasures,
  VisitorDrawerState,
  doesClientRequireSafetyGuardsAtom,
  visitorDrawerStateAtom,
  visitorMeasuresAtom,
} from '../state';

/**
 * Time to wait before resolving decision to auto-open the measures drawer. This
 * buffer gives the client's device additional time to await the response from
 * the outstanding measures query, if it's still in flight, but with a hard
 * deadline so as to prevent a jarring experience of the drawer opening long
 * after the client has already loaded into the waiting room. If we have not
 * resolved the query by the deadline, we will open the drawer optimistically to
 * a loading state. The client will see the measures completed screen if it
 * eventually turns out they have no outstanding measures.
 */
export const DRAWER_OPEN_BUFFER_MS = 1500;

type Props = {
  /** Optional callback to call when the measures drawer is auto-opened. */
  onAutoOpen?: () => void;
};

export type Dependencies = {
  logger: Logger;
  useFeatureGate: typeof useFeatureGateBase;
  useIsScreenBelowBreakpoint: typeof useIsScreenBelowBreakpointBase;
  useParams: typeof useParamsBase;
  useTrackEvent: typeof useTrackEventBase;
};

const DEFAULT_DEPENDENCIES: Dependencies = {
  logger: loggerBase,
  useFeatureGate: useFeatureGateBase,
  useIsScreenBelowBreakpoint: useIsScreenBelowBreakpointBase,
  useParams: useParamsBase,
  useTrackEvent: useTrackEventBase,
};

/**
 * Utility hook to auto-opens the measures drawer when a client joins the
 * waiting room, provided the feature gate is enabled and the screen is large
 * enough for the split-screen push drawer layout (>= sm breakpoint). The drawer
 * auto-opens after a buffer period even when measures haven't finished loading
 * yet, to avoid a jarring experience of the drawer opening several seconds
 * *after* the client loads into the waiting room. However, if the query has
 * already resolved with zero outstanding measures by the time this effect runs,
 * the drawer is skipped.
 *
 * If the screen is too small, logs an info message instead of opening. Fires at
 * most once per mount.
 *
 * @param props - Primary props for the hook:
 * - onAutoOpen: Optional callback to call when the measures drawer is
 *   auto-opened.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 */
export function useAutoOpenMeasuresDrawer(
  { onAutoOpen }: Props = {},
  { logger, useFeatureGate, useIsScreenBelowBreakpoint, useParams, useTrackEvent }: Dependencies = DEFAULT_DEPENDENCIES,
) {
  const { userViewed } = useTrackEvent();
  const { patientShortId = '' } = useParams();

  const getDrawerState = useAtomCallback(useCallback((get) => get(visitorDrawerStateAtom), []));
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);
  const hasAutoOpenedRef = useRef(false);

  const { value: isAutoOpenEnabled } = useFeatureGate(telehealthAutoOpenIncompleteMeasuresDrawer);
  const getDoesClientRequireSafetyGuards = useAtomCallback(
    useCallback((get) => get(doesClientRequireSafetyGuardsAtom), []),
  );
  const isBelowSmBreakpoint = useIsScreenBelowBreakpoint('sm');
  const getVisitorMeasures = useAtomCallback(useCallback((get) => get(visitorMeasuresAtom), []));

  const maybeOpenDrawer = useCallback(() => {
    if (hasAutoOpenedRef.current) return;
    if (!isAutoOpenEnabled || !patientShortId) return;

    const visitorMeasures = getVisitorMeasures();

    // Do not open the drawer if the client requires safety guards.
    if (getDoesClientRequireSafetyGuards()) {
      logSkippedAutoOpen(logger, 'client requires safety guards', patientShortId, visitorMeasures);
      return;
    }

    // Do not open the drawer if it's already open.
    const drawerState = getDrawerState();
    if (drawerState) {
      logSkippedAutoOpen(logger, 'drawer already open', patientShortId, visitorMeasures, { drawerState });
      return;
    }

    // We'll set this ref even if we skip auto-opening due to screen size or
    // having no outstanding measures, to make future effect runs
    // short-circuit sooner.
    hasAutoOpenedRef.current = true;

    if (isBelowSmBreakpoint) {
      logSkippedAutoOpen(logger, 'screen too small for split-screen', patientShortId, visitorMeasures);
      return;
    }

    // If the measures query already resolved with nothing outstanding, skip
    // opening. When measures are still loading (undefined), we optimistically
    // open to avoid a jarring late-open on slower networks/devices.
    if (visitorMeasures && !visitorMeasures.length) {
      logSkippedAutoOpen(logger, 'no outstanding measures', patientShortId, visitorMeasures);
      return;
    }

    setDrawerState(VisitorDrawerState.PENDING_FORMS);
    logAutoOpen(logger, patientShortId, visitorMeasures);
    onAutoOpen?.();
    userViewed(EventTypeEnum.CLIENT_MEASURES_AUTO_OPENED, { patientShortId });
  }, [
    getDoesClientRequireSafetyGuards,
    getDrawerState,
    getVisitorMeasures,
    hasAutoOpenedRef,
    isAutoOpenEnabled,
    isBelowSmBreakpoint,
    logger,
    onAutoOpen,
    patientShortId,
    setDrawerState,
    userViewed,
  ]);

  useEffect(
    function autoOpenMeasuresDrawerEffect() {
      if (hasAutoOpenedRef.current) return;
      if (!isAutoOpenEnabled || !patientShortId) return;

      const visitorMeasures = getVisitorMeasures();

      // If measures are still loading, give them a chance to resolve before
      // making a decision.
      if (!visitorMeasures) {
        logger.info('Measures are still loading, deferring auto-open decision', {
          patientShortId,
          bufferMs: DRAWER_OPEN_BUFFER_MS,
        });
        setTimeout(maybeOpenDrawer, DRAWER_OPEN_BUFFER_MS);
        return;
      }

      maybeOpenDrawer();
    },
    [getVisitorMeasures, isAutoOpenEnabled, logger, maybeOpenDrawer, patientShortId],
  );
}

function logSkippedAutoOpen(
  logger: Logger,
  reason: string,
  patientShortId: string,
  outstandingMeasures: OutstandingMeasures | undefined,
  otherProps: Record<string, unknown> = {},
) {
  logger.info(`Skipping auto-open of measures drawer: ${reason}`, {
    patientShortId,
    outstandingMeasuresLoaded: outstandingMeasures !== undefined,
    outstandingMeasuresCount: outstandingMeasures?.length ?? 0,
    ...otherProps,
  });
}

function logAutoOpen(logger: Logger, patientShortId: string, outstandingMeasures: OutstandingMeasures | undefined) {
  logger.info('Auto-opening measures drawer', {
    patientShortId,
    outstandingMeasuresLoaded: outstandingMeasures !== undefined,
    outstandingMeasuresCount: outstandingMeasures?.length ?? 0,
  });
}
